import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Badge, Box, Typography, Tooltip, IconButton } from '@mui/material';
import {
  GridViewOutlined,
  AppsOutlined,
  PeopleOutlined,
  ListAltOutlined,
  BuildOutlined,
  LogoutOutlined,
  DarkModeOutlined,
  LightModeOutlined,
  ChevronRightOutlined,
  LocalOfferOutlined,
  BarChartOutlined,
  SchoolOutlined,
  HelpOutlineOutlined,
  ArticleOutlined,
  NotificationsOutlined,
  CampaignOutlined,
  SmartToyOutlined,
  HealthAndSafetyOutlined,
  TuneOutlined,
  ManageAccountsOutlined,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import LogoIcon from './LogoIcon';
import TutorialModal, { TutorialState } from './TutorialModal';
import SpotlightGuide, { PAGE_GUIDES } from './SpotlightGuide';
import AssistantBubble from './AssistantBubble';
import TimezoneSetupModal from './TimezoneSetupModal';

const W = 220;

const NAV = [
  { label: 'Dashboard',    icon: GridViewOutlined,   path: '/' },
  { label: 'Applications', icon: AppsOutlined,       path: '/apps' },
  { label: 'Analytics',    icon: BarChartOutlined,   path: '/analytics',  roles: ['admin', 'super_admin'] },
  { label: 'Requests',     icon: NotificationsOutlined, path: '/requests', roles: ['admin', 'super_admin'] },
  { label: 'Pricing',      icon: LocalOfferOutlined, path: '/pricing',    perm: 'can_view_logs' },
  { label: 'Users',        icon: PeopleOutlined,     path: '/users',      perm: 'can_manage_admins' },
  { label: 'Logs',         icon: ListAltOutlined,    path: '/logs',       perm: 'can_view_logs' },
  { label: 'Operations',   icon: BuildOutlined,      path: '/operations', perm: 'can_reset_tokens' },
  // Status is visible to every authenticated role — an app_viewer sees only
  // probes for their assigned apps (scoped server-side).
  { label: 'Status',       icon: HealthAndSafetyOutlined, path: '/status' },
  { label: 'Changelogs',   icon: CampaignOutlined,   path: '/changelogs', roles: ['super_admin'] },
  // Assistant appears whenever the user has been granted access. Admins/super
  // admins always have it; viewers/app_viewers see it once a super admin flips
  // the flag on their user record.
  { label: 'Assistant',    icon: SmartToyOutlined,   path: '/assistant',  showIf: 'assistant' as const },
  // Super-admin-only config surface: OpenAI key, caps, model change (2-approval).
  { label: 'Assistant Config', icon: TuneOutlined,   path: '/assistant/config', roles: ['super_admin'] },
];

const ROLE_BADGE: Record<string, { label: string; color: string }> = {
  super_admin: { label: 'Super Admin', color: '#FF1A50' },
  admin:       { label: 'Admin',       color: '#F59E0B' },
  viewer:      { label: 'Viewer',      color: '#888888' },
  app_viewer:  { label: 'App Viewer',  color: '#3B82F6' },
};

const ROLE_CONSOLE: Record<string, string> = {
  super_admin: 'Super Admin Console',
  admin:       'Admin Console',
  viewer:      'Viewer Portal',
  app_viewer:  'App Viewer Portal',
};

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const navigate  = useNavigate();
  const location  = useLocation();
  const isDark = mode === 'dark';

  const [tutorialState, setTutorialState] = useState<TutorialState>('closed');
  const [tutorialStep, setTutorialStep]   = useState(0);
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  const [pendingCount, setPendingCount]   = useState(0);
  const [failingEndpoints, setFailingEndpoints] = useState(0);

  const isAuthed = !!user?.role;

  useEffect(() => {
    // Every authenticated user connects: admins/viewers get pending_count and
    // failing_endpoints_count; app_viewers get a failing count scoped to their
    // apps (backend-side) so the Status badge lights up for them too.
    if (!isAuthed) return;

    const token = localStorage.getItem('admin_token');
    if (!token) return;

    const apiBase = process.env.REACT_APP_API_URL ?? 'http://localhost:8001';
    const wsBase = apiBase.replace(/^https:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');

    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let alive = true;

    function connect() {
      if (!alive) return;
      try {
        ws = new WebSocket(`${wsBase}/ws/notifications?token=${token}`);

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'pending_count') setPendingCount(data.count);
            else if (data.type === 'failing_endpoints_count') setFailingEndpoints(data.count);
          } catch { /* ignore */ }
        };

        ws.onclose = () => {
          if (alive) reconnectTimer = setTimeout(connect, 5000);
        };

        ws.onerror = () => ws?.close();
      } catch { /* ignore */ }
    }

    connect();

    return () => {
      alive = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, [isAuthed]);

  const currentRoute = location.pathname;
  const hasPageGuide = !!PAGE_GUIDES[currentRoute];

  // Auto-open once per user (tracked in localStorage — non-sensitive UI preference)
  useEffect(() => {
    if (!user?.email) return;
    const key = `tutorial_seen_${user.email}`;
    if (!localStorage.getItem(key)) {
      setTutorialStep(0);
      setTutorialState('open');
      localStorage.setItem(key, '1');
    }
  }, [user?.email]);

  const bg    = isDark ? '#0A0A0A' : '#FFFFFF';
  const bgMain = isDark ? '#0A0A0A' : '#F5F5F5';
  const border = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const textPrimary = isDark ? '#EFEFEF' : '#0A0A0A';
  const textMuted   = isDark ? '#555555' : '#AAAAAA';
  const textSec     = isDark ? '#888888' : '#666666';
  const hoverBg     = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';

  const visible = NAV.filter(n =>
    (!n.perm  || user?.permissions?.[n.perm as keyof typeof user.permissions]) &&
    (!n.roles || n.roles.includes(user?.role ?? '')) &&
    (n.showIf !== 'assistant' || !!user?.assistant_enabled)
  );

  const active = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const currentPage = visible.find(n => active(n.path))?.label ?? 'Dashboard';
  const initials = (user?.name ?? 'A').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const role = ROLE_BADGE[user?.role ?? 'viewer'] ?? { label: user?.role ?? 'Viewer', color: '#888888' };
  const consoleLabel = ROLE_CONSOLE[user?.role ?? ''] ?? 'Console';

  React.useEffect(() => {
    document.title = `LLM Wrapper — ${consoleLabel}`;
  }, [consoleLabel]);

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: bgMain }}>

      {/* ─── Sidebar ─────────────────────────────────────────────── */}
      <Box sx={{
        width: W,
        flexShrink: 0,
        position: 'fixed',
        left: 0, top: 0,
        height: '100vh',
        bgcolor: bg,
        borderRight: `1px solid ${border}`,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 100,
      }}>

        {/* Brand */}
        <Box
          sx={{
            px: 3, pt: 4, pb: 3,
            display: 'flex', alignItems: 'center', gap: 1.5,
            cursor: 'pointer',
            '&:hover .brand-name': { opacity: 0.7 },
            transition: 'opacity 0.15s',
          }}
          onClick={() => navigate('/')}
        >
          <LogoIcon size={22} letterColor={isDark ? '#EFEFEF' : '#FF1A50'} />
          <Box>
            <Typography className="brand-name" sx={{
              fontWeight: 700, fontSize: '0.875rem',
              color: textPrimary, letterSpacing: '-0.02em',
              lineHeight: 1.2, transition: 'opacity 0.15s',
            }}>
              LLM Wrapper
            </Typography>
            <Typography sx={{
              fontSize: '0.65rem', color: textMuted,
              fontWeight: 500, letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}>
              {consoleLabel}
            </Typography>
          </Box>
        </Box>

        {/* Nav */}
        <Box sx={{ flex: 1, px: 2, py: 1, overflowY: 'auto' }}>
          <Typography sx={{
            fontSize: '0.65rem', fontWeight: 600,
            color: textMuted, letterSpacing: '0.08em',
            textTransform: 'uppercase', px: 1.5, mb: 1,
          }}>
            Menu
          </Typography>

          {visible.map((item) => {
            const on = active(item.path);
            const Icon = item.icon;
            const showBadge = item.path === '/requests' && pendingCount > 0;
            const showStatusBadge = item.path === '/status' && failingEndpoints > 0;
            return (
              <Box
                key={item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  px: 1.5, py: 1.25,
                  mb: 0.25,
                  borderRadius: '7px',
                  cursor: 'pointer',
                  position: 'relative',
                  bgcolor: on ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)') : 'transparent',
                  transition: 'all 0.1s ease',
                  '&:hover': {
                    bgcolor: on
                      ? (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')
                      : hoverBg,
                  },
                }}
              >
                {on && (
                  <Box sx={{
                    position: 'absolute', left: 0, top: '25%', bottom: '25%',
                    width: '2px', bgcolor: '#FF1A50', borderRadius: '0 2px 2px 0',
                  }} />
                )}
                {showBadge ? (
                  <Badge badgeContent={pendingCount} color="warning" max={99}
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}>
                    <Icon sx={{ fontSize: 17, color: on ? textPrimary : '#F59E0B', flexShrink: 0 }} />
                  </Badge>
                ) : showStatusBadge ? (
                  <Badge badgeContent={failingEndpoints} color="error" max={99}
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}>
                    <Icon sx={{ fontSize: 17, color: on ? textPrimary : '#EF4444', flexShrink: 0 }} />
                  </Badge>
                ) : (
                  <Icon sx={{ fontSize: 17, color: on ? textPrimary : textSec, flexShrink: 0, transition: 'color 0.1s' }} />
                )}
                <Typography sx={{
                  fontSize: '0.875rem',
                  fontWeight: on || showBadge || showStatusBadge ? 600 : 400,
                  color: on
                    ? textPrimary
                    : showBadge
                      ? '#F59E0B'
                      : showStatusBadge
                        ? '#EF4444'
                        : textSec,
                  flex: 1,
                  transition: 'all 0.1s',
                  letterSpacing: '-0.01em',
                }}>
                  {item.label}{showBadge ? ` (${pendingCount})` : showStatusBadge ? ` (${failingEndpoints})` : ''}
                </Typography>
              </Box>
            );
          })}
        </Box>


        {/* Docs + Tutorial buttons */}
        <Box sx={{ px: 2, pb: 1 }}>
          <Box
            onClick={() => navigate('/docs')}
            sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              px: 1.5, py: 1.25, borderRadius: '7px', cursor: 'pointer',
              transition: 'all 0.1s ease',
              '&:hover': { bgcolor: hoverBg },
            }}
          >
            <ArticleOutlined sx={{ fontSize: 17, color: textSec, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 400, color: textSec, letterSpacing: '-0.01em' }}>
              API Docs
            </Typography>
          </Box>
          <Tooltip title="Restart tutorial" placement="right" arrow>
            <Box
              onClick={() => { setTutorialStep(0); setTutorialState('open'); }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                px: 1.5, py: 1.25, borderRadius: '7px', cursor: 'pointer',
                transition: 'all 0.1s ease',
                '&:hover': { bgcolor: hoverBg },
              }}
            >
              <SchoolOutlined sx={{ fontSize: 17, color: textSec, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 400, color: textSec, letterSpacing: '-0.01em' }}>
                Tutorial
              </Typography>
            </Box>
          </Tooltip>
        </Box>

        {/* User section */}
        <Box sx={{
          px: 2, py: 2.5,
          borderTop: `1px solid ${border}`,
        }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 1.5, py: 1,
            borderRadius: '7px',
            '&:hover': { bgcolor: hoverBg },
            transition: 'background 0.1s',
            cursor: 'default',
          }}>
            <Box sx={{
              width: 30, height: 30, borderRadius: '6px', flexShrink: 0,
              bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden',
            }}>
              {user?.photo_url ? (
                <Box
                  component="img"
                  src={user.photo_url}
                  alt={user.name}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: textPrimary, letterSpacing: '0.02em' }}>
                  {initials}
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontSize: '0.82rem', fontWeight: 600,
                color: textPrimary, overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                letterSpacing: '-0.01em', lineHeight: 1.3,
              }}>
                {user?.name ?? 'Administrator'}
              </Typography>
              <Typography sx={{
                fontSize: '0.67rem', fontWeight: 500,
                color: role.color, letterSpacing: '0.02em',
                textTransform: 'uppercase',
              }}>
                {role.label}
              </Typography>
            </Box>
            <Tooltip title="Profile & preferences" placement="top" arrow>
              <IconButton
                onClick={() => navigate('/profile')}
                size="small"
                sx={{
                  p: 0.75, color: textMuted,
                  '&:hover': { color: textPrimary, bgcolor: hoverBg },
                }}
              >
                <ManageAccountsOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sign out" placement="top" arrow>
              <IconButton
                onClick={logout}
                size="small"
                sx={{
                  p: 0.75, color: textMuted,
                  '&:hover': { color: '#EF4444', bgcolor: 'rgba(239,68,68,0.08)' },
                }}
              >
                <LogoutOutlined sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <TutorialModal
        state={tutorialState}
        step={tutorialStep}
        role={user?.role ?? 'viewer'}
        onClose={() => setTutorialState('closed')}
        onMinimize={() => setTutorialState('minimized')}
        onExpand={() => setTutorialState('open')}
        onStepChange={setTutorialStep}
      />

      <SpotlightGuide
        open={spotlightOpen}
        route={currentRoute}
        onClose={() => setSpotlightOpen(false)}
      />

      {/*
        First-login timezone prompt: shown once, when the loaded user has no
        stored timezone. `required` locks the modal so the user can't Esc past
        it — they must confirm/pick before proceeding. Save flow calls
        refreshUser, so once user.timezone is populated this stops rendering.
      */}
      <TimezoneSetupModal
        open={isAuthed && !user?.timezone}
        onClose={() => { /* no-op — required=true blocks dismissal */ }}
        required
      />

      {/* ─── Main ────────────────────────────────────────────────── */}
      <Box component="main" sx={{
        flex: 1,
        ml: `${W}px`,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
      }}>

        {/* Top bar */}
        <Box sx={{
          height: 52,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,
          borderBottom: `1px solid ${border}`,
          bgcolor: isDark ? '#0A0A0A' : '#FFFFFF',
        }}>
          {/* Breadcrumb */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.8rem', color: textMuted, fontWeight: 400 }}>
              Console
            </Typography>
            <ChevronRightOutlined sx={{ fontSize: 14, color: textMuted }} />
            <Typography sx={{ fontSize: '0.8rem', color: textPrimary, fontWeight: 500, letterSpacing: '-0.01em' }}>
              {currentPage}
            </Typography>
          </Box>

          {/* Right actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* System status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 6, height: 6, borderRadius: '50%',
                bgcolor: '#22C55E',
              }} />
              <Typography sx={{ fontSize: '0.75rem', color: textMuted, fontWeight: 400 }}>
                Online
              </Typography>
            </Box>

            <Box sx={{ width: '1px', height: 16, bgcolor: border }} />

            {/* Page guide — Learn button */}
            <Tooltip title={hasPageGuide ? 'Learn this page' : 'No guide for this page'} arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={!hasPageGuide}
                  onClick={() => setSpotlightOpen(true)}
                  sx={{
                    p: 0.75,
                    color: hasPageGuide ? textMuted : (isDark ? '#2A2A2A' : '#DDD'),
                    '&:hover': { color: '#FF1A50', bgcolor: 'rgba(255,26,80,0.07)' },
                    '&.Mui-disabled': { color: isDark ? '#2A2A2A' : '#DDD' },
                  }}
                >
                  <HelpOutlineOutlined sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>

            {/* Theme toggle */}
            <Tooltip title={isDark ? 'Light mode' : 'Dark mode'} arrow>
              <IconButton
                size="small"
                onClick={toggleTheme}
                sx={{
                  p: 0.75, color: textMuted,
                  '&:hover': { color: textPrimary },
                }}
              >
                {isDark
                  ? <LightModeOutlined sx={{ fontSize: 16 }} />
                  : <DarkModeOutlined sx={{ fontSize: 16 }} />
                }
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Page content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }}>
          <div key={location.pathname} className="page-transition">
            <Outlet />
          </div>
        </Box>
      </Box>
      <AssistantBubble />
    </Box>
  );
};

export default Layout;
